import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { ArrowLeft, Mail, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [directResetLink, setDirectResetLink] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    setLoading(true);
    
    try {
      const res = await api.post('/auth/forgot-password', { email });
      // Backend still returns a reset_link so users can proceed even if email delivery fails
      if (res.data?.reset_link) {
        setDirectResetLink(res.data.reset_link);
      }
      setEmailSent(true);
      toast.success('Password reset instructions sent!');
    } catch (error) {
      // Even on error, show success message for security (don't reveal if email exists)
      setEmailSent(true);
    } finally {
      setLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4" data-testid="forgot-password-success">
        <Card className="w-full max-w-md p-8 bg-slate-800 border-slate-700">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-500" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2" data-testid="email-sent-title">Check Your Email</h1>
            <p className="text-slate-300 mb-6">
              If an account exists for <strong className="text-white">{email}</strong>, you will receive password reset instructions shortly.
            </p>
            <p className="text-sm text-slate-400 mb-6">
              Didn't receive an email? Check your spam folder or use the direct link below.
            </p>
            {directResetLink && (
              <div className="mb-6 p-4 bg-slate-900/70 border border-amber-500/40 rounded-md text-left" data-testid="direct-reset-link-block">
                <p className="text-xs uppercase tracking-wider text-amber-400 mb-2 font-semibold">Direct reset link</p>
                <a
                  href={directResetLink}
                  data-testid="direct-reset-link"
                  className="text-blue-300 hover:text-blue-200 underline break-all text-sm"
                >
                  {directResetLink}
                </a>
                <p className="text-xs text-slate-400 mt-2">Click the link (valid for 1 hour) to set a new password.</p>
              </div>
            )}
            <div className="space-y-3">
              <Button
                onClick={() => navigate('/login')}
                variant="default"
                className="w-full bg-blue-600 hover:bg-blue-700"
                data-testid="return-to-login-btn"
              >
                Return to Login
              </Button>
              <Button
                onClick={() => {
                  setEmailSent(false);
                  setEmail('');
                  setDirectResetLink('');
                }}
                variant="outline"
                className="w-full border-slate-600 text-slate-300 hover:bg-slate-700"
                data-testid="try-another-email-btn"
              >
                Try Another Email
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4" data-testid="forgot-password-page">
      <Card className="w-full max-w-md p-8 bg-slate-800 border-slate-700">
        <div className="mb-6">
          <Button
            onClick={() => navigate('/login')}
            variant="ghost"
            className="text-slate-400 hover:text-white mb-4"
            data-testid="back-to-login-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Login
          </Button>
          <h1 className="text-3xl font-bold text-white mb-2" data-testid="forgot-password-title">Forgot Password?</h1>
          <p className="text-slate-400">
            No worries! Enter your email address and we'll send you instructions to reset your password.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="forgot-password-form">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Email Address
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="pl-10 bg-slate-700 border-slate-600 text-white placeholder-slate-400 focus:border-blue-500"
                disabled={loading}
                autoFocus
                data-testid="forgot-password-email-input"
              />
            </div>
          </div>

          <Button
            type="submit"
            className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            disabled={loading}
            data-testid="forgot-password-submit-btn"
          >
            {loading ? 'Sending...' : 'Send Reset Instructions'}
          </Button>
        </form>
      </Card>
    </div>
  );
};

export default ForgotPasswordPage;